<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { usePaymentEditDialog } from '@/domains/billing/composables/usePaymentEditDialog';
import { useStripeElements } from '@/domains/billing/composables/useStripeElements';
import { getSubBus } from '@/platform/host-sdk/host-bridge';
import { invalidateAccountInfo } from '@/platform/identity/queries';
import { DocsLinks } from '@/shared/docs';
import { queryClient } from '@/shared/lib/query-client';
import { HOST_EVENTS, INVALIDATE_RESOURCE, emitMessage } from '@celerdata/host-bridge-contract';
import { Open } from '@celerdata/icons';

const { t } = useI18n();
const dialog = usePaymentEditDialog();
const { status, error, submitting, attach, confirm, detach } = useStripeElements();

const mountTarget = ref<HTMLElement | null>(null);

const title = computed(() =>
  dialog.payload.value?.mode === 'edit'
    ? t('billing.payment.directly.editTitle')
    : t('billing.payment.directly.addTitle'),
);
const submitLabel = computed(() =>
  dialog.payload.value?.mode === 'edit' ? t('common.edit') : t('common.add'),
);

const visible = computed({
  get: () => dialog.isOpen.value,
  set: (next) => {
    if (!next) onClose();
  },
});

watch(
  () => dialog.isOpen.value,
  async (open) => {
    if (!open) {
      detach();
      return;
    }
    // Wait for ElDialog teleport + DOM mount of the target div before
    // handing it to Stripe. Stripe Element's `mount(el)` reads the
    // container's layout synchronously to seed iframe dimensions.
    await nextTick();
    const el = mountTarget.value;
    const secret = dialog.payload.value?.clientSecret;
    if (!el || !secret) return;
    await attach(el, secret);
  },
);

function onClose(): void {
  detach();
  dialog.close();
}

async function onSubmit(): Promise<void> {
  if (submitting.value || status.value !== 'ready') return;
  const result = await confirm();
  if (!result.ok) {
    // Toast on card_error / validation_error / declines so the user
    // knows why submit didn't advance — silent failure leaves the
    // dialog looking stuck.
    ElMessage.error(result.error.message ?? t('common.unknownErr'));
    return;
  }
  emitMessage(getSubBus(), HOST_EVENTS.INVALIDATE, {
    resource: INVALIDATE_RESOURCE.BILLING_PAYMENT_CARD,
  });
  // Also invalidate host-owned accountInfo so AppBanner / TrialDialog
  // pick up the new payment state (remindCode) — vue-query's 5-min
  // stale window would otherwise leave the "add credit card" banner
  // visible after Stripe confirm succeeds.
  invalidateAccountInfo(queryClient);
  onClose();
}
</script>

<template>
  <ElDialog
    v-model="visible"
    :title="title"
    width="640px"
    :close-on-click-modal="false"
    append-to-body
    data-testid="payment-edit-dialog"
    @close="onClose"
    @closed="dialog.dispose"
  >
    <p class="text-fg-2 mb-3 text-sm leading-5">
      {{ t('billing.payment.directly.addDesc', { 0: '' }) }}
      <a
        class="text-brand-azure inline-flex items-center gap-1 hover:underline"
        :href="DocsLinks.UnderstandBilling"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('billing.payment.directly.moreLink') }}
        <ElIcon :size="14"><Open /></ElIcon>
      </a>
    </p>

    <ElAlert
      v-if="status === 'error'"
      type="error"
      :closable="false"
      :title="error?.message ?? t('common.unknownErr')"
      class="mb-4"
    />

    <div ref="mountTarget" data-testid="payment-edit-stripe-mount"></div>

    <template #footer>
      <ElButton text data-testid="payment-edit-cancel" @click="onClose">
        {{ t('common.cancel') }}
      </ElButton>
      <ElButton
        type="primary"
        :loading="submitting"
        :disabled="status !== 'ready'"
        data-testid="payment-edit-submit"
        @click="onSubmit"
      >
        {{ submitLabel }}
      </ElButton>
    </template>
  </ElDialog>
</template>
